import React, { useState } from 'react';
import { Tabs, Card, DatePicker, Select, Button, Table, Statistic, Row, Col } from 'antd';
import { DollarOutlined, RiseOutlined, UserAddOutlined, LineChartOutlined } from '@ant-design/icons';
import './AnalyticsReports.css';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

function AnalyticsReports() {
  const [activeTab, setActiveTab] = useState('revenue');

  const revenueColumns = [
    { title: 'Date', dataIndex: 'date', key: 'date' },
    { title: 'Agency', dataIndex: 'agency', key: 'agency' },
    { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: (val) => `$${val}` },
    { title: 'Transactions', dataIndex: 'transactions', key: 'transactions' },
  ];

  const campaignColumns = [
    { title: 'Campaign', dataIndex: 'campaign', key: 'campaign' },
    { title: 'Impressions', dataIndex: 'impressions', key: 'impressions' },
    { title: 'Clicks', dataIndex: 'clicks', key: 'clicks' },
    { title: 'CTR', dataIndex: 'ctr', key: 'ctr', render: (val) => `${val}%` },
    { title: 'Spend', dataIndex: 'spend', key: 'spend', render: (val) => `$${val}` },
  ];

  const growthColumns = [
    { title: 'Metric', dataIndex: 'metric', key: 'metric' },
    { title: 'Current', dataIndex: 'current', key: 'current' },
    { title: 'Previous', dataIndex: 'previous', key: 'previous' },
    { title: 'Growth', dataIndex: 'growth', key: 'growth', render: (val) => `${val}%` },
  ];

  return (
    <div className="analytics-reports-container">
      <div className="page-header">
        <h1>Analytics & Reports</h1>
        <p>View revenue, campaign performance, and platform growth metrics</p>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} className="analytics-tabs">
        <TabPane
          tab={
            <span>
              <DollarOutlined />
              Revenue Reports
            </span>
          }
          key="revenue"
        >
          <Card>
            <div className="filters">
              <RangePicker style={{ marginRight: 16 }} />
              <Select defaultValue="all" style={{ width: 200, marginRight: 16 }}>
                <Option value="all">All Agencies</Option>
                <Option value="agency1">Agency 1</Option>
                <Option value="agency2">Agency 2</Option>
              </Select>
              <Button type="primary">Generate Report</Button>
            </div>

            <Row gutter={16} style={{ marginTop: 24, marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Revenue"
                    value={125840}
                    prefix="$"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="This Month"
                    value={28450}
                    prefix="$"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Active Subscriptions"
                    value={156}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Growth"
                    value={12.5}
                    suffix="%"
                    prefix={<RiseOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>

            <Table
              columns={revenueColumns}
              dataSource={[]}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <LineChartOutlined />
              Campaign Performance
            </span>
          }
          key="campaigns"
        >
          <Card>
            <div className="filters">
              <RangePicker style={{ marginRight: 16 }} />
              <Select defaultValue="all" style={{ width: 200, marginRight: 16 }}>
                <Option value="all">All Campaigns</Option>
                <Option value="active">Active</Option>
                <Option value="completed">Completed</Option>
              </Select>
              <Button type="primary">Generate Report</Button>
            </div>

            <Row gutter={16} style={{ marginTop: 24, marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Campaigns"
                    value={342}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Active Campaigns"
                    value={89}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Impressions"
                    value={2450000}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Avg CTR"
                    value={3.2}
                    suffix="%"
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
            </Row>

            <Table
              columns={campaignColumns}
              dataSource={[]}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <UserAddOutlined />
              Platform Growth Metrics
            </span>
          }
          key="growth"
        >
          <Card>
            <div className="filters">
              <RangePicker style={{ marginRight: 16 }} />
              <Select defaultValue="monthly" style={{ width: 200, marginRight: 16 }}>
                <Option value="daily">Daily</Option>
                <Option value="weekly">Weekly</Option>
                <Option value="monthly">Monthly</Option>
              </Select>
              <Button type="primary">Generate Report</Button>
            </div>

            <Row gutter={16} style={{ marginTop: 24, marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Users"
                    value={1842}
                    prefix={<UserAddOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="New Users (30d)"
                    value={234}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Active Agencies"
                    value={67}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="User Growth"
                    value={18.7}
                    suffix="%"
                    prefix={<RiseOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>

            <Table
              columns={growthColumns}
              dataSource={[]}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
}

export default AnalyticsReports;
